import { Inject, Injectable } from "@angular/core";
import { CalAngularService } from "@cvx/cal-angular";
import { HttpClient } from "@angular/common/http";
import {
  BehaviorSubject,
  firstValueFrom,
  interval,
  lastValueFrom,
  Observable,
  of,
  Subscription,
  tap,
  from,
} from "rxjs";
import { AuthStatus } from "../models/auth-status";
import {
  LOCAL_STORAGE,
  SESSION_STORAGE,
  StorageService,
} from "ngx-webstorage-service";
import { AccountInfo } from "@azure/msal-browser";
import { SlbTokenResponse } from "../models/slb-token-response";
import { environment } from "../../environments/environment";
import { catchError, map, switchMap } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class AuthService {
  private calConfig = {
    redirectUri: (() => {
      const url = new URL(window.location.href);
      const port = !!url.port ? `:${url.port}` : "";
      return `${url.protocol}//${url.hostname}${port}`;
    })(),
  };
  private signingIn = false;
  
  private _currentAuthStatus = new BehaviorSubject<AuthStatus>({
    userLoggedIn: false,
    statusMessage: "authentication not attempted yet",
  });
  currentUser: AccountInfo | null = null;

  get currentAuthStatus(): Observable<AuthStatus> {
    return this._currentAuthStatus.asObservable();
  }

  constructor(
    private httpClient: HttpClient,
    private calAngularService: CalAngularService,
    @Inject(SESSION_STORAGE) private sessionStorage: StorageService<string>,
    @Inject(LOCAL_STORAGE) private localStorage: StorageService<string>,
    private window: Window,
  ) {
    this.currentAuthStatus
      .pipe(
        switchMap((status) => {
          return interval(environment.refreshTokenIntervalInMillis).pipe(
            switchMap((s) => {
              return this.tryAuthenticateFromRefreshToken();
            }),
          );
        }),
      )
      .subscribe(console.log);
  }

  authenticateUser(): Observable<boolean> {
    return this.calAngularService.isUserSignedIn().pipe(
      switchMap((isUserSignedIn) => {
        if (isUserSignedIn === true) {
          this.currentUser = this.calAngularService.getAccount();
          // not authenticated with slb now
          //return this.tryAuthenticateFromRefreshToken();
          this.localStorage.set("username", this.currentUser?.username);
          // get token and set in local storage for interceptor to use
          // now move to auth interceptor to get the token dynamically
          // this.calAngularService.getAADToken(this.getScopes()).pipe(
          //       map(tokenResult => {
          //         const token = typeof tokenResult === 'string' ? tokenResult : tokenResult?.accessToken;
          //         if(token) {
          //           this.localStorage.set('auth_token', token);
          //         }
          //       })).subscribe({
          //         next: () => {
          //           console.log("get token and set in local storage");
          //         },
          //         error: (err) => {
          //           console.error("error getting token and setting in local storage", err);
          //         }
          //       });
          this._currentAuthStatus.next({
            userLoggedIn: true,
            statusMessage: "authenticated",
            redirectTo: ``,
            //redirectTo: `wells`,
          });
          return of(true);
        } else {
          // Prevent concurrent sign-in attempts
          if (this.signingIn) {
            console.warn("Sign-in already in progress, skipping duplicate attempt");
            return of(false);
          }

          this.signingIn = true;
          this._currentAuthStatus.next({
            userLoggedIn: false,
            statusMessage: "cal authentication failed! initiating sign-in",
          });
          return this.calAngularService.userInitiatedSignIn().pipe(
            map((p) => {
              throw new Error("cal authentication being done");
            }),
          );
        }
      }),
    );
  }

  tryAuthenticateFromRefreshToken(): Observable<boolean> {
    const refreshToken = this.localStorage.get("slbRefreshToken");
    if (!!refreshToken) {
      return this.httpClient
        .post<SlbTokenResponse>(
          `${environment.apiEndpoint}/authentication/RefreshSlbToken`,
          {
            refreshToken: refreshToken,
            redirectUrl: this.calConfig.redirectUri,
          },
        )
        .pipe(
          switchMap((refreshSlbToken) => {
            this.localStorage.set("slbToken", refreshSlbToken?.authToken);
            this.localStorage.set(
              "slbRefreshToken",
              refreshSlbToken?.refreshToken,
            );
            return this.handleRefreshTokenResponse(
              !!refreshSlbToken?.authToken && !!refreshSlbToken?.refreshToken,
            );
          }),
          catchError((err) => {
            console.error({ err });
            return this.handleRefreshTokenResponse(false);
          }),
        );
    }
    return this.handleRefreshTokenResponse(false);
  }

  handleRefreshTokenResponse(
    authenticateFromRefreshToken: boolean,
  ): Observable<boolean> {
    if (authenticateFromRefreshToken) {
      this._currentAuthStatus.next({
        userLoggedIn: true,
        statusMessage: "authenticated from slb refresh token",
      });
      return of(true);
    }
    return this.tryAuthenticateFromCodeInUrl();
  }

  tryAuthenticateFromCodeInUrl(): Observable<boolean> {
    const url = new URL(this.window.location.href);
    const code = url.searchParams.get("code");
    if (!!code) {
      return this.httpClient
        .post<SlbTokenResponse>(
          `${environment.apiEndpoint}/authentication/SlbToken`,
          { code: code, redirectUrl: this.calConfig.redirectUri },
        )
        .pipe(
          switchMap((slbToken) => {
            this.localStorage.set("slbToken", slbToken.authToken);
            this.localStorage.set("slbRefreshToken", slbToken.refreshToken);
            if (!!slbToken?.authToken && !!slbToken?.refreshToken) {
              return this.handleCodeResponse(true);
            } else {
              this._currentAuthStatus.next({
                userLoggedIn: false,
                statusMessage: "authentication from slb code failed!",
              });
              return of(false);
            }
          }),
          catchError((err) => {
            console.error({ err });
            this.localStorage.set("slbToken", "");
            this.localStorage.set("slbRefreshToken", "");
            this._currentAuthStatus.next({
              userLoggedIn: false,
              statusMessage: "authentication from slb code gave error!",
            });
            return of(false);
          }),
        );
    }
    return this.handleCodeResponse(false);
  }

  handleCodeResponse(authenticateFromCode: boolean): Observable<boolean> {
    if (authenticateFromCode) {
      const url = new URL(this.window.location.href);
      const state = url.searchParams.get("state");
      const search = !!state ? this.sessionStorage.get(state) : "";
      this._currentAuthStatus.next({
        userLoggedIn: true,
        statusMessage: "authenticated from slb code",
        redirectTo: `${state}${!!search ? search : ""}`,
      });
      return of(true);
    }
    return this.redirectToSlbAuth();
  }

  redirectToSlbAuth(): Observable<boolean> {
    const url = new URL(this.window.location.href);
    const currentPathRequested = `${url.pathname}`;
    this.sessionStorage.set(currentPathRequested, `${url.search}`);
    return this.httpClient
      .post<{
        authCodeUrl: string;
      }>(`${environment.apiEndpoint}/authentication/SlbAuthCodeUrl`, { redirectUrl: this.calConfig.redirectUri, state: currentPathRequested })
      .pipe(
        map((authCodeUrlRes) => {
          this._currentAuthStatus.next({
            userLoggedIn: false,
            statusMessage: "redirecting to slb for auth!",
          });
          this.window.location.href = authCodeUrlRes.authCodeUrl.replace(
            /"/g,
            "",
          );
          return true;
        }),
      );
  }

  public getScopes(): string[] {
    return [`${environment.calClientId}/user_impersonation`];
  }
}
